/**
 * Single date-range picker with calendar dropdown.
 * Opens one calendar where the user selects a start and end date visually.
 */

import { CalendarIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { format } from 'date-fns';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatToISODate, parseISODate } from '@/utils/dateUtils';

interface DateRangePickerProps {
  /** Start date as ISO string (YYYY-MM-DD) or empty string */
  startDate: string;
  /** End date as ISO string (YYYY-MM-DD) or empty string */
  endDate: string;
  /** Callback when the range changes */
  onRangeChange: (startDate: string, endDate: string) => void;
  /** Placeholder when no range is selected */
  placeholder?: string;
  /** Additional className for the trigger button */
  className?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

export const DateRangePicker = ({
  startDate,
  endDate,
  onRangeChange,
  placeholder = 'Select date range',
  className,
  disabled = false,
}: DateRangePickerProps) => {
  const [open, setOpen] = useState(false);

  // Convert ISO strings to DateRange for react-day-picker
  const selected: DateRange | undefined =
    startDate || endDate
      ? {
          from: startDate ? parseISODate(startDate) : undefined,
          to: endDate ? parseISODate(endDate) : undefined,
        }
      : undefined;

  const handleSelect = (range: DateRange | undefined) => {
    if (!range) {
      onRangeChange('', '');
      return;
    }
    const newStart = range.from ? formatToISODate(range.from) : '';
    const newEnd = range.to ? formatToISODate(range.to) : '';
    onRangeChange(newStart, newEnd);

    // Auto-close once both dates are selected
    if (range.from && range.to) {
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRangeChange('', '');
  };

  const hasRange = startDate || endDate;
  let displayText = placeholder;
  if (startDate && endDate) {
    displayText = `${format(parseISODate(startDate), 'MMM d, yyyy')} – ${format(parseISODate(endDate), 'MMM d, yyyy')}`;
  } else if (startDate) {
    displayText = `${format(parseISODate(startDate), 'MMM d, yyyy')} – …`;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex items-center gap-2',
            'h-8 px-3 text-xs',
            'rounded-lg border border-zinc-950/10 dark:border-white/10',
            'bg-white dark:bg-zinc-900',
            'hover:border-zinc-950/20 dark:hover:border-white/20',
            'focus:outline-none focus:border-zinc-950/20 dark:focus:border-white/20',
            'transition-colors',
            hasRange
              ? 'text-zinc-950 dark:text-white'
              : 'text-zinc-500 dark:text-zinc-500',
            disabled && 'opacity-50 cursor-not-allowed',
            className,
          )}
        >
          <CalendarIcon className="size-3.5 text-gray-500 dark:text-zinc-500 shrink-0" />
          <span className="truncate font-medium">{displayText}</span>
          {hasRange && !disabled && (
            <XMarkIcon
              className="size-3.5 text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300 shrink-0"
              onClick={handleClear}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4}>
        <Calendar
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          numberOfMonths={1}
          className="p-2"
        />
      </PopoverContent>
    </Popover>
  );
};
